import { AutoAwesome, AutoGraph, Hub } from "@mui/icons-material";
import { Box, Stack, Typography, alpha, styled } from "@mui/material";
import slice1 from "Assets/backgrounds/login/slice-1.png";
import { motion } from "motion/react";
import { ReactNode } from "react";

const PageRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  height: "100dvh",
  flexDirection: "column-reverse",
  overflowY: "auto",
  [theme.breakpoints.up("md")]: {
    flexDirection: "row",
  },
}));

const HeroPanel = styled(Box)(({ theme }) => ({
  flex: "0 0 44%",
  padding: theme.spacing(8, 7),
  minHeight: "fit-content",
  position: "relative",
  overflow: "hidden",
  display: "none",
  flexDirection: "column",
  justifyContent: "center",
  color: theme.palette.common.white,
  backgroundColor: theme.palette.primary.dark,
  backgroundImage: `
    linear-gradient(145deg, ${alpha(theme.palette.primary.dark, 0.97)} 0%, ${alpha("#3a1670", 0.94)} 50%, ${alpha(theme.palette.primary.dark, 0.9)} 100%),
    url(${slice1})
  `,
  backgroundPosition: "left top, -80px top",
  backgroundRepeat: "no-repeat, no-repeat",
  backgroundSize: "cover, 520px",
  [theme.breakpoints.up("md")]: {
    display: "flex",
  },
}));

const FormPanel = styled(Box)(({ theme }) => ({
  flex: 1,
  minHeight: "fit-content",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(4, 3),
  flexDirection: "column",
  backgroundColor: theme.palette.primary.dark,
  backgroundImage: `
    linear-gradient(145deg, ${alpha(theme.palette.primary.dark, 0.97)} 0%, ${alpha("#3a1670", 0.94)} 50%, ${alpha(theme.palette.primary.dark, 0.9)} 100%),
    url(${slice1})
  `,
  [theme.breakpoints.up("md")]: {
    padding: theme.spacing(6),
    flexDirection: "row",
    background: theme.palette.grey[50],
    backgroundSize: "280px",
  },
}));

const FormHeroHeader = styled(Box)(({ theme }) => ({
  display: "none",
  [theme.breakpoints.down("md")]: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4, 0),
  },
}));

const FormCard = styled(Box)(({ theme }) => ({
  width: "100%",
  maxWidth: 510,
  padding: theme.spacing(4, 3.5),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.common.white,
  boxShadow: `0 24px 80px ${alpha(theme.palette.primary.dark, 0.12)}`,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(5, 4.5),
  },
}));

const GlowOrb = styled(motion.div)(({ theme }) => ({
  position: "absolute",
  borderRadius: "50%",
  filter: "blur(60px)",
  background: alpha(theme.palette.primary.light, 0.35),
  pointerEvents: "none",
}));

const BenefitRow = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  alignItems: "flex-start",
  gap: theme.spacing(2),
}));

const BenefitIcon = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 40,
  height: 40,
  borderRadius: theme.shape.borderRadius,
  flexShrink: 0,
  backgroundColor: alpha(theme.palette.common.white, 0.12),
  color: theme.palette.common.white,
}));

const HeroTitle = styled("h1")(({ theme }) => ({
  color: theme.palette.common.white,
  margin: 0,
  marginTop: theme.spacing(1.5),
  fontSize: 42,
  fontWeight: 700,
  lineHeight: 1.1,
  textShadow: `0 2px 24px ${alpha(theme.palette.common.black, 0.25)}`,
}));

const FormHeroTitle = styled("h1")(({ theme }) => ({
  color: theme.palette.common.white,
  margin: 0,
  fontSize: 32,
  fontWeight: 700,
  lineHeight: 1.15,
  textShadow: `0 2px 24px ${alpha(theme.palette.common.black, 0.25)}`,
}));

const HeroBody = styled(Typography)(({ theme }) => ({
  color: theme.palette.common.white,
  fontSize: 16,
  lineHeight: 1.6,
  opacity: 0.92,
  [theme.breakpoints.up("md")]: {
    fontSize: 18,
  },
}));

const BenefitTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.common.white,
  fontWeight: 600,
  lineHeight: 1.3,
}));

const BenefitBody = styled(Typography)(({ theme }) => ({
  color: theme.palette.common.white,
  marginTop: theme.spacing(0.5),
  opacity: 0.82,
}));

const BENEFITS = [
  {
    icon: <AutoGraph fontSize="small" />,
    title: "Unified ecommerce reporting",
    body: "All your key metrics in one place - revenue, content, orders, customers, LTV, and more.",
  },
  {
    icon: <Hub fontSize="small" />,
    title: "Attribution you can trust",
    body: "Server-side tracking and first-party data deliver accurate attribution across every touchpoint.",
  },
  {
    icon: <AutoAwesome fontSize="small" />,
    title: "AI-powered insights & optimization",
    body: "Get actionable recommendations that grow your business, & optimize your store with AB testing and more.",
  },
] as const;

interface IWooCommerceSignupLayoutProps {
  children: ReactNode;
  platformName?: string;
}

function WooCommerceSignupLayout({
  children,
  platformName = "WooCommerce",
}: IWooCommerceSignupLayoutProps) {
  return (
    <PageRoot>
      <HeroPanel>
        <GlowOrb
          style={{ width: 220, height: 220, top: "8%", right: "-40px" }}
          animate={{ y: [0, 24, 0], opacity: [0.35, 0.55, 0.35] }}
          transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
        />
        <GlowOrb
          style={{ width: 180, height: 180, bottom: "12%", left: "-50px" }}
          animate={{ y: [0, -18, 0], opacity: [0.25, 0.45, 0.25] }}
          transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
        />

        <Stack
          spacing={4}
          sx={{ position: "relative", zIndex: 1, maxWidth: 640 }}
        >
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.1 }}
          >
            <HeroTitle>
              Upgrade your{" "}
              <Box component="span" sx={{ display: "block" }}>
                {platformName} analytics
              </Box>
            </HeroTitle>
            <HeroBody sx={{ mt: 2 }}>
              Incendium connects to your store to unlock the power of
              first-party data. Get trusted attribution, unified reporting, and
              AI-powered insights that drive smarter decisions.
            </HeroBody>
          </motion.div>

          <Stack spacing={2.5}>
            {BENEFITS.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.45, delay: 0.2 + index * 0.1 }}
              >
                <BenefitRow>
                  <BenefitIcon>{benefit.icon}</BenefitIcon>
                  <Box>
                    <BenefitTitle variant="body1">{benefit.title}</BenefitTitle>
                    <BenefitBody variant="body2">{benefit.body}</BenefitBody>
                  </Box>
                </BenefitRow>
              </motion.div>
            ))}
          </Stack>
        </Stack>
      </HeroPanel>

      <FormPanel>
        <FormHeroHeader>
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.1 }}
          >
            <FormHeroTitle>
              Upgrade your{" "}
              <Box component="span" sx={{ display: "block" }}>
                {platformName} analytics
              </Box>
            </FormHeroTitle>
            <HeroBody sx={{ mt: 2 }}>
              Incendium connects to your store to unlock the power of
              first-party data. Get trusted attribution, unified reporting, and
              AI-powered insights that drive smarter decisions.
            </HeroBody>
          </motion.div>
        </FormHeroHeader>
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.15 }}
          style={{ width: "100%", display: "flex", justifyContent: "center" }}
        >
          <FormCard>{children}</FormCard>
        </motion.div>
      </FormPanel>
    </PageRoot>
  );
}

export default WooCommerceSignupLayout;
